"use client";
import { Room } from "livekit-client";
import { useRouter } from "next/navigation";
import { useCallback, useRef } from "react";
import { Logger } from "@shared/utils/logger";
import type { MeetingEndPayload } from "@client/types/livekit.types";

interface UseMeetingEndStateOptions {
  room: Room;
  onIntentionalLeave?: () => void;
  participantId?: string;
  participantIdentity?: string;
  onBeforeMeetingEnd?: () => Promise<void>;
}

interface UseMeetingEndStateReturn {
  handleMeetingEnd: (payload: MeetingEndPayload) => Promise<void> /** Handle meeting end event */;
}

/**
 * Manages meeting ended state.
 * When meeting-end data channel message is received, shows a toast and redirects to meeting-ended page.
 */
export const useMeetingEndState = ({
  room,
  onIntentionalLeave,
  participantId,
  participantIdentity,
  onBeforeMeetingEnd
}: UseMeetingEndStateOptions): UseMeetingEndStateReturn => {
  const router = useRouter();
  const hasHandledRef = useRef(false);

  /**
   * Handle meeting end event from data channel.
   * Shows toast notification and redirects to meeting-ended page.
   */
  const handleMeetingEnd = useCallback(
    async (payload: MeetingEndPayload) => {
      // Prevent duplicate handling
      if (hasHandledRef.current) return;
      hasHandledRef.current = true;

      try {
        await onBeforeMeetingEnd?.();
      } catch (error) {
        Logger.warn("useMeetingEndState", "Final session artifact save failed", error);
      }

      // Signal intentional leave to suppress connection error overlay
      if (onIntentionalLeave) {
        onIntentionalLeave();
      }

      // Build redirect URL
      const roomName = payload.roomName || room.name;
      const hash = typeof window !== "undefined" ? window.location.hash : "";
      let url = `/meeting-ended?room=${encodeURIComponent(roomName)}&ended=true`;

      // Add participantId for review functionality
      const effectiveParticipantId = participantId || participantIdentity || room.localParticipant.identity;

      if (effectiveParticipantId) {
        url += `&participantId=${encodeURIComponent(effectiveParticipantId)}`;
      } else {
        Logger.warn("useMeetingEndState", "participantId is missing from connectionDetails");
      }

      if (hash) {
        url += `&ref=${encodeURIComponent(hash)}`;
      }

      // Navigate first (prevents connection lost overlay)
      router.push(url);

      // Then disconnect the room
      try {
        room.disconnect();
      } catch (e) {
        Logger.warn("useMeetingEndState", "Error disconnecting room:", e);
      }
    },
    [room, router, onIntentionalLeave, participantId, participantIdentity, onBeforeMeetingEnd]
  );

  return {
    handleMeetingEnd
  };
};
